// PHA-1225: Clinic Settings card for the Program Confirmation Form (consent by text): the
// text message and the Yes/No form it links to, per language. Separate from the welcome SMS,
// which stays as it is (and can be turned off).
// Zack (2026-10-28): every language the clinic supports can have its own wording; until it
// does, it is sent with the clinic's English wording.
import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Box, Button, Callout, Card, Flex, Heading, Text, TextArea, TextField } from '@radix-ui/themes';
import { CheckIcon } from '@radix-ui/react-icons';
import { toast } from 'react-hot-toast';
import { clinicApi } from '../../services/api';
import {
  CONSENT_FORM_TITLE,
  ConsentWording,
  CONSENT_QUESTION,
  DEFAULT_CONSENT_FORM_TEXT,
  DEFAULT_CONSENT_SMS_TEMPLATE,
  effectiveConsentWording,
  fillConsentText,
  hasOwnConsentWording,
} from '../../utils/consentText';

export interface ConsentLanguage extends ConsentWording {
  code: string;
  name: string;
  native_name?: string;
}

interface Props {
  /** The clinic's English wording (organizations.consent_*). */
  english: ConsentWording;
  /** The clinic's other enabled languages, with their own wording. */
  languages: ConsentLanguage[];
  clinicName: string;
  onSaved: (language: string, saved: ConsentWording) => void;
}

const ENGLISH = 'en-US';
const RTL_LANGUAGES = ['he-IL', 'ar-001'];
const SAMPLE_LINK = 'https://ccm.phanova.com/survey/consent/…';

const ConsentSettingsEditor: React.FC<Props> = ({ english, languages, clinicName, onSaved }) => {
  const [language, setLanguage] = useState(ENGLISH);
  const current = languages.find((l) => l.code === language);
  // A language that was turned off while selected: back to English.
  useEffect(() => {
    if (language !== ENGLISH && !current) setLanguage(ENGLISH);
  }, [language, current]);

  // What this language sends today, and what "no text of its own" falls back to. Saving the
  // fallback text unchanged stores nothing, so it keeps following later changes.
  const fallback = useMemo(
    () =>
      language === ENGLISH
        ? { smsTemplate: DEFAULT_CONSENT_SMS_TEMPLATE, formText: DEFAULT_CONSENT_FORM_TEXT, question: CONSENT_QUESTION }
        : effectiveConsentWording(null, english),
    [language, english],
  );
  const saved = useMemo(
    () => (language === ENGLISH ? effectiveConsentWording(null, english) : effectiveConsentWording(current, english)),
    [language, current, english],
  );
  const usesEnglish = language !== ENGLISH && !hasOwnConsentWording(current);

  const [smsTemplate, setSmsTemplate] = useState(saved.smsTemplate);
  const [formText, setFormText] = useState(saved.formText);
  const [question, setQuestion] = useState(saved.question);
  const [saving, setSaving] = useState(false);

  // Keyed on the values, not the object: an unrelated change to the languages list (e.g. a
  // survey SMS saved below) must not throw away what is being typed here.
  useEffect(() => {
    setSmsTemplate(saved.smsTemplate);
    setFormText(saved.formText);
    setQuestion(saved.question);
  }, [language, saved.smsTemplate, saved.formText, saved.question]);

  const smsMissingLink = smsTemplate.trim() !== '' && !smsTemplate.includes('{link}');
  const dirty =
    smsTemplate.trim() !== saved.smsTemplate ||
    formText.trim() !== saved.formText ||
    question.trim() !== saved.question;

  const save = async () => {
    if (smsMissingLink) {
      toast.error('The text message must contain {link}');
      return;
    }
    setSaving(true);
    try {
      const asStored = (value: string, fallbackValue: string) =>
        value.trim() === fallbackValue ? '' : value.trim();
      const res = await clinicApi.updateConsentSettings({
        language,
        consentSmsTemplate: asStored(smsTemplate, fallback.smsTemplate),
        consentFormText: asStored(formText, fallback.formText),
        consentQuestion: asStored(question, fallback.question),
      });
      toast.success(`${CONSENT_FORM_TITLE} wording saved`);
      onSaved(language, {
        consent_sms_template: res?.consent_sms_template ?? null,
        consent_form_text: res?.consent_form_text ?? null,
        consent_question: res?.consent_question ?? null,
      });
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save the wording');
    } finally {
      setSaving(false);
    }
  };

  const languageName = language === ENGLISH ? 'English' : current?.name ?? language;
  const previewLines = fillConsentText(formText, { firstName: 'Maria', clinicName }).split('\n');
  const previewDir = RTL_LANGUAGES.includes(language) ? 'rtl' : 'ltr';

  return (
    <Card className="mb-4" data-testid="consent-settings-editor">
      <Box p="4">
        <Flex align="center" justify="between" gap="3" wrap="wrap">
          <Box>
            <Heading size="4">{CONSENT_FORM_TITLE}</Heading>
            <Text size="2" color="gray">
              Sent from the patient chart (Actions → Send {CONSENT_FORM_TITLE}). The patient answers
              Yes or No on a short form; the answer shows on the chart under Consent Status and can be
              opened as a PDF or sent to the EHR. It does not replace the verbal consent.
            </Text>
          </Box>
          <Flex gap="2">
            <Button
              variant="soft"
              color="gray"
              onClick={() => {
                setSmsTemplate(fallback.smsTemplate);
                setFormText(fallback.formText);
                setQuestion(fallback.question);
              }}
              disabled={saving}
            >
              {language === ENGLISH ? "Use Phanova's wording" : 'Use the English wording'}
            </Button>
            <Button onClick={save} disabled={!dirty || saving || smsMissingLink} data-testid="consent-settings-save">
              <CheckIcon />
              {saving ? 'Saving...' : `Save ${languageName} Wording`}
            </Button>
          </Flex>
        </Flex>

        {languages.length > 0 && (
          <Flex gap="2" wrap="wrap" align="center" className="mt-3" data-testid="consent-language-tabs">
            <Text size="2" weight="medium">Language:</Text>
            {[{ code: ENGLISH, name: 'English' } as ConsentLanguage, ...languages].map((l) => (
              <Button
                key={l.code}
                size="1"
                variant={l.code === language ? 'solid' : 'soft'}
                color={l.code === language ? undefined : 'gray'}
                onClick={() => setLanguage(l.code)}
                disabled={saving}
                data-testid={`consent-language-${l.code}`}
              >
                {l.name}
                {l.code !== ENGLISH && !hasOwnConsentWording(l) && (
                  <Badge size="1" color="gray" variant="surface">uses English</Badge>
                )}
              </Button>
            ))}
          </Flex>
        )}
        {usesEnglish && (
          <Callout.Root color="amber" size="1" className="mt-3">
            <Callout.Text>
              No {languageName} wording yet, so patients who get the form in {languageName} see the
              English wording below. Translate it and save to send it in {languageName}.
            </Callout.Text>
          </Callout.Root>
        )}

        <Flex gap="4" className="mt-4" direction={{ initial: 'column', md: 'row' }}>
          <Flex direction="column" gap="3" style={{ flex: 1, minWidth: 0 }}>
            <Box>
              <Text size="2" weight="medium" className="mb-1 block">Text message</Text>
              <TextArea rows={4} value={smsTemplate} onChange={(e) => setSmsTemplate(e.currentTarget.value)} />
              <Text size="1" color={smsMissingLink ? 'red' : 'gray'} className="mt-1 block">
                Must contain <code>{'{link}'}</code> (the form). Also available: <code>{'{first_name}'}</code>,{' '}
                <code>{'{clinic_name}'}</code>.
              </Text>
            </Box>
            <Box>
              <Text size="2" weight="medium" className="mb-1 block">Form text</Text>
              <TextArea rows={9} value={formText} onChange={(e) => setFormText(e.currentTarget.value)} />
              <Text size="1" color="gray" className="mt-1 block">
                Start a line with "- " for a bullet. Changes apply to forms sent after you save.
              </Text>
            </Box>
            <Box>
              <Text size="2" weight="medium" className="mb-1 block">Yes / No question</Text>
              <TextField.Root>
                <TextField.Input
                  value={question}
                  onChange={(e) => setQuestion(e.currentTarget.value)}
                  data-testid="consent-settings-question"
                />
              </TextField.Root>
              <Text size="1" color="gray" className="mt-1 block">
                Shown above the Yes and No buttons. The buttons and the thank-you message are in the
                form's language.
              </Text>
            </Box>
          </Flex>
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Text size="2" weight="medium" className="mb-1 block">Preview</Text>
            <Callout.Root color="gray" className="mb-3">
              <Callout.Text>
                {smsMissingLink ? 'Add {link} to the text message.' : fillConsentText(smsTemplate, { firstName: 'Maria', clinicName, link: SAMPLE_LINK })}
              </Callout.Text>
            </Callout.Root>
            <Box className="border rounded p-3" dir={previewDir}>
              {/* The page greets in the form's language; only the English greeting is shown here. */}
              {language === ENGLISH && <Text as="p" size="3" weight="bold" className="mb-2">Hi Maria,</Text>}
              {previewLines.map((line, i) => {
                const t = line.trim();
                if (!t) return null;
                return /^[-*•]\s+/.test(t)
                  ? <Text key={i} as="p" size="2" className="mb-1">• {t.replace(/^[-*•]\s+/, '')}</Text>
                  : <Text key={i} as="p" size="2" className="mb-2">{t}</Text>;
              })}
              <Text as="p" size="2" weight="bold" className="mt-3">
                {fillConsentText(question || fallback.question, { firstName: 'Maria', clinicName })}
              </Text>
              <Flex gap="2" className="mt-2">
                <Button color="green" style={{ flex: 1 }} tabIndex={-1}>Yes</Button>
                <Button color="red" variant="outline" style={{ flex: 1 }} tabIndex={-1}>No</Button>
              </Flex>
            </Box>
          </Box>
        </Flex>
      </Box>
    </Card>
  );
};

export default ConsentSettingsEditor;
